import mainStyle from "../mainStyle.module.scss";
import React, { useState, useEffect } from "react";
import { cloneDeep } from "lodash";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { TextField, InputAdornment, Grid, Box, Divider, Typography, Button, Tooltip } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";

import btn_general from "assets/images/icons/btn_general.svg";
import btn_general_light from "assets/images/icons/btn_general_light.svg";
import btn_wireless from "assets/images/icons/btn_wireless.svg";
import btn_wireless_light from "assets/images/icons/btn_wireless_light.svg";
import btn_wired from "assets/images/icons/btn_wired.svg";
import btn_wired_light from "assets/images/icons/btn_wired_light.svg";
import btn_captive_portal from "assets/images/icons/btn_captive_portal.svg";
import btn_captive_portal_light from "assets/images/icons/btn_captive_portal_light.svg";
import btn_alert from "assets/images/icons/btn_alert.svg";
import btn_alert_light from "assets/images/icons/btn_alert_light.svg";
import btn_profiles from "assets/images/icons/btn_profiles.svg";
import btn_profiles_light from "assets/images/icons/btn_profiles_light.svg";
import icn_expandright from "assets/images/icons/icn_expandright.svg";

import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin_red.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";
import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as ScheduleIcon } from "assets/images/icons/btn_schedule.svg";
import { ReactComponent as ScheduleLightIcon } from "assets/images/icons/btn_schedule_light.svg";
import { ReactComponent as ProIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

// Const
import { leftMenu } from "./const.js";

// Service
import { useUserContext } from "contexts/UserContext";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useDataSourceManager from "hooks/useDataSourceManager";
import useAuthScope from "hooks/useAuthScope";
import { useUnsavedChangesBlocker } from 'hooks/useUnsavedChangesBlocker';
import useZustandStore from "zustandStore";

// Component
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import AutocompleteSelect from "components/Select/AutocompleteSelect";
import TextAreaField from "components/TextAreaField";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";

import ParentMenu from "./parentMenu.js";
import SubMenu from "./subMenu.js";
import ModalforCheck from "../modalCheck.js";
import ModalConfirmPw from "../modalConfirmPw";

import WirelessSSID from "./Wireless/SSID";
import WirelessRadio from "./Wireless/Radio";
import WiredBasic from "./Wired/Basic";
import VoiceVlan from "./Wired/VoiceVlan";
import CaptivePortal from "./CaptivePortal";
import Notifications from "./Notifications";
import WiredPortManagement from "./Wired/PortManagement";
import WiredConfigProvisioning from "./Wired/ConfigProvisioning";
import WiredQualityOfService from "./Wired/QualityOfService";
import WiredIPv4ACL from "./Wired/IPv4ACL";
import ProfilesPort from "./Profiles/Port";
import ProfileSchedule from "./Profiles/Schedule";
import ProfileMacAddress from "./Profiles/MacAddress";
import ProfileIpAddress from "./Profiles/IpAddress";
import ProfileUserAccount from "./Profiles/UserAccount";
import ProfileRadiusServer from "./Profiles/RadiusServer";
import ProfileLdapServer from "./Profiles/LdapServer";
import ScheduledReboot from "./ScheduledReboot";

// APIs and data normalization used for parent-level Pro icon state
import WiredQosApi from "./Wired/QualityOfService/util/api";
import { normalizeQosItems } from "./Wired/QualityOfService/util/func";
import WiredIpv4AclApi from "./Wired/IPv4ACL/util/api";
import ProfileIpAddressApi from "./Profiles/IpAddress/util/api";
import * as ProfileLdapServerApi from "./Profiles/LdapServer/helper";

import { getCountryList, getTimezoneList } from "utils/timezone";
import { validateDeviceAccount, validateDeviceAccountPassword } from 'utils/validate';

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  width: "22px",
  height: "22px",
  minWidth: "16px",
  padding: "0",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const SiteLevel = () => {
  const unSavedBlocker = useUnsavedChangesBlocker();
  const { tabBlocker } = unSavedBlocker;
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const isUnityProSubscribed = useZustandStore(
    (state) => state.serviceStatus?.services?.unityPro === true
  );

  const { scopeData, setScopeData, scopeReady } = useOutletContext();
  const { setBread } = useBreadStorageContext();
  const { userState } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const { dataMap, getData, sendRequest, sendBatch } =
    useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const [nowAnchor, setNowAnchor] = useState("account");

  const handleAnchor = tabBlocker((section) => {
    if (section === 'account') {
      fetchGeneralData();
    }

    setNowAnchor(section);
    setOpenParent(null);
    navigate(location.pathname, { replace: true, state: {} });
  });

  const handleSetNowAnchor = tabBlocker((key) => {
    setNowAnchor(key);
    navigate(location.pathname, { replace: true, state: {} });
  });

  const [nowScope, setNowScope] = useState("");
  const [checkScopeName, setCheckScopeName] = useState(true);

  const [showEditGeneral, setShowEditGeneral] = useState(true);
  const [showDeleteGeneral, setShowDeleteGeneral] = useState(true);
  // const [showInventory, setShowInventory] = useState(true);

  const [openModalConfirmPw, setOpenModalConfirmPw] = useState(false);
  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [modalSaveBtn, setModalSaveBtn] = useState("");
  const [modalCancelBtn, setModalCancelBtn] = useState("");
  const [originUser, setOriginUser] = useState("");
  // for modalCheck to check from where
  const [modalFrom, setModalFrom] = useState("");

  const [updateNameUuid, _setUpdateNameUuid] = useState(uuidv4());
  const [deleteNameUuid, _setDeleteNameUuid] = useState(uuidv4());
  const [readInventoryUuid, _setReadInventoryUuid] = useState(uuidv4());
  const [updateInventoryUuid, _setUpdateInventoryUuid] = useState(uuidv4());

  const [nowUuid, setNowUuid] = useState("");

  const [openParent, setOpenParent] = useState(null);

  const [isAddHovered, setIsAddHovered] = useState(false);
  const [showDevicePw, setShowDevicePw] = useState(false);
  const [userError, setUserError] = useState("");
  const [userPWError, setUserPWError] = useState("");

  const [readSiteGeneralUuid, _setReadSiteGeneralUuid] = useState(uuidv4());
  const [updateSiteGeneralUuid, _setUpdateSiteGeneralUuid] = useState(uuidv4());
  const [readWiredQosUuid, _setReadWiredQosUuid] = useState(uuidv4());
  const [readWiredIpv4AclUuid, _setReadWiredIpv4AclUuid] = useState(uuidv4());
  const [readIpAddressProfileUuid, _setReadIpAddressProfileUuid] = useState(uuidv4());
  const [readLdapProfileUuid, _setReadLdapProfileUuid] = useState(uuidv4());
  const [qosHasNonDefaultValue, setQosHasNonDefaultValue] = useState(false);
  const [ipv4AclRuleCount, setIpv4AclRuleCount] = useState(0);
  const [ipAddressProfileCount, setIpAddressProfileCount] = useState(0);
  const [ldapProfileCount, setLdapProfileCount] = useState(0);

  // 原始資料
  const [originalInfoData, setOriginalInfoData] = useState({});
  const [originalCountryDomainData, setOriginalCountryDomainData] = useState({});
  const [originalTimezoneList, setOriginalTimezoneList] = useState([]);
  const [originalTimezoneData, setOriginalTimezoneData] = useState({});
  const [originalNtpServers, setOriginalNtpServers] = useState([]);
  const [originalDeviceAccountData, setOriginalDeviceAccountData] = useState({});
  // 當前填入的資料
  const [scopeName, setScopeName] = useState('');
  const [selectCountryList, setSelectCountryList] = useState([]);
  const [selectCountry, setSelectCountry] = useState({});
  const [timezoneList, setTimezoneList] = useState([]);
  const [selectTimezoneData, setSelectTimezoneData] = useState({});
  const [ntpServers, setNtpServers] = useState([]);
  const [addressData, setAddressData] = useState('');
  const [inventoryName, setInventoryName] = useState('');
  const [inventoryPW, setInventoryPW] = useState('');

  // Save location state
  const [savedLocationState, setSavedLocationState] = useState(null);

  useEffect(() => {
    if (!isAuthReady || !serviceStatus || isUnityProSubscribed) {
      return;
    }

    setQosHasNonDefaultValue(false);
    setIpv4AclRuleCount(0);
    setIpAddressProfileCount(0);
    setLdapProfileCount(0);

    const qosRequest = WiredQosApi.init(auth).getRequestParams(WiredQosApi.actionTypeEnum.read);
    const ipv4AclRequest = WiredIpv4AclApi.init(auth).getRequestParams(WiredIpv4AclApi.actionTypeEnum.read);
    const ipAddressRequest = ProfileIpAddressApi.init(auth).getRequestParams(ProfileIpAddressApi.actionTypeEnum.read);
    const ldapRequest = ProfileLdapServerApi.getMainSendRequestParams({ auth, method: "read" });
    let cancelled = false;

    const fetchProIconData = async () => {
      const { getSendBatchData } = await sendBatch([
        {
          apiKey: qosRequest.apiKey,
          requestUuid: readWiredQosUuid,
          restfulPlaceholders: qosRequest.params,
        },
        {
          apiKey: ipv4AclRequest.apiKey,
          requestUuid: readWiredIpv4AclUuid,
          restfulPlaceholders: ipv4AclRequest.params,
        },
        {
          apiKey: ipAddressRequest.apiKey,
          requestUuid: readIpAddressProfileUuid,
          restfulPlaceholders: ipAddressRequest.params,
        },
        {
          apiKey: ldapRequest.apiUrl,
          requestUuid: readLdapProfileUuid,
          restfulPlaceholders: ldapRequest.requestParams,
        },
      ]);

      if (cancelled) return;

      const qosResponse = getSendBatchData({ requestUuid: readWiredQosUuid });
      if (qosResponse && !qosResponse.hasOwnProperty("code")) {
        setQosHasNonDefaultValue(normalizeQosItems(qosResponse.data).some((item) => item.modified));
      }

      const ipv4AclResponse = getSendBatchData({ requestUuid: readWiredIpv4AclUuid });
      if (ipv4AclResponse && !ipv4AclResponse.error && (!ipv4AclResponse.hasOwnProperty("code") || [0, 200].includes(ipv4AclResponse.code))) {
        setIpv4AclRuleCount((ipv4AclResponse.data || []).length);
      }

      const ipAddressResponse = getSendBatchData({ requestUuid: readIpAddressProfileUuid });
      if (ipAddressResponse && !ipAddressResponse.hasOwnProperty("code")) {
        setIpAddressProfileCount((ipAddressResponse.data || []).length);
      }

      const ldapResponse = getSendBatchData({ requestUuid: readLdapProfileUuid });
      const ldapResponseData = ldapResponse?.data;
      if (ldapResponseData && (!ldapResponseData.hasOwnProperty("code") || ldapResponseData.code === 200)) {
        setLdapProfileCount(ProfileLdapServerApi.getInitTableData({ responseData: ldapResponseData }).length);
      }
    };

    void fetchProIconData();

    return () => {
      cancelled = true;
    };
  }, [auth, isAuthReady, isUnityProSubscribed, readIpAddressProfileUuid, readLdapProfileUuid, readWiredIpv4AclUuid, readWiredQosUuid, sendBatch, serviceStatus]);

  const handleParentClick = tabBlocker((parent) => {
    setOpenParent((prev) => (prev === parent ? null : parent));
    setNowAnchor(null);
    navigate(location.pathname, { replace: true, state: {} });
  });

  const handleGoToPage = (tabName, subTabName) => {
    // Special-case: navigate to captive portal which is a top-level section
    if (subTabName === 'captive-portal' || tabName === 'captive-portal') {
      handleAnchor('captive-portal');
      return;
    }

    setOpenParent(tabName);
    // Safely resolve subKey for profiles-like parents, fallback to subTabName if not found
    let resolvedAnchor = subTabName;
    try {
      if (leftMenu[tabName] && leftMenu[tabName].getSubKey) {
        resolvedAnchor = leftMenu[tabName].getSubKey(leftMenu[tabName].subKey[subTabName]);
      }
    } catch (e) {
      // fallback to provided subTabName
      resolvedAnchor = subTabName;
    }

    setNowAnchor(resolvedAnchor);
    navigate(location.pathname, { replace: true, state: {} });
  };

  // useEffect(() => {
  //   if (location?.state?.navigateTab === "wired-port-management") {
  //     setOpenParent('wired');
  //     setNowAnchor('wired-portManagement');
  //   } else if (location?.state?.navigateTab === "wireless-ssid") {
  //     setOpenParent('wireless');
  //     setNowAnchor('wireless-ssid');
  //   }
  // }, [location])

  useEffect(() => {
    if (userState) {
      setOriginUser(userState);
    }
  }, [userState]);

  useEffect(() => {
    let nowSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );
    if ((Object.keys(nowSelectedBread).length !== 0 && scopeReady) || checkMockup === 'true') {
      setShowDeleteGeneral(false);
      setShowEditGeneral(false);

      setNowScope("Site");
      // setScopeName(nowSelectedBread.site.name); // admin
      if (nowSelectedBread.site.roleId === 9) {
        setShowEditGeneral(true);
      }
      if (Object.keys(nowSelectedBread.orgs).length !== 0) {
        if (
          nowSelectedBread.orgs.roleId === 5 ||
          nowSelectedBread.orgs.roleId === 6
        ) {
          setShowDeleteGeneral(true);
        }
      } else {
        // orgs.length = 0
        if (
          nowSelectedBread.realm.roleId === 5 ||
          nowSelectedBread.realm.roleId === 6
        ) {
          setShowDeleteGeneral(true);
        }
      }
      // readInventory();
    }
  }, [scopeReady]);

  useEffect(() => {
    fetchGeneralData();
  }, [nowScope])

  // dataMap 先分開做 未來有機會一次整合全部的 dataMap 行為
  useEffect(() => {
    const response = getData(readSiteGeneralUuid);
    if (response && response.data) {
      const { info, countryDomain, timeConfig, deviceAccount } = response.data;

      setOriginalInfoData(info)
      setScopeName(info.siteName)
      setAddressData(info.address)

      const currentSelectCountryList = getCountryList();
      setSelectCountryList(currentSelectCountryList)

      const currentCountry = currentSelectCountryList.find((item) => item.value === countryDomain.countryIndex)
      setOriginalCountryDomainData(currentCountry)
      setSelectCountry(currentCountry);

      const timezoneList = getTimezoneList(countryDomain.countryIndex);
      setOriginalTimezoneList(timezoneList)
      setTimezoneList(timezoneList)
      const currentTimezoneData = timezoneList.find((item) => item.value === timeConfig.timezone)
      setOriginalTimezoneData(currentTimezoneData)
      setSelectTimezoneData(currentTimezoneData)

      const formattedData = [timeConfig.ntpServer1, timeConfig.ntpServer2, timeConfig.ntpServer3].filter(Boolean);
      setOriginalNtpServers(formattedData)
      setNtpServers(formattedData)

      setOriginalDeviceAccountData(deviceAccount);
      setInventoryName(deviceAccount.userName)
      setInventoryPW(deviceAccount.password)

    }
  }, [dataMap])

  useEffect(() => {
    if (location?.state?.navigateTab) {
      const tab = location.state.navigateTab;
      if (tab === "wired-port-management") {
        setSavedLocationState(cloneDeep(location.state));
        setOpenParent('wired');
        setNowAnchor('wired-portManagement');
      } else if (tab === "wireless-ssid") {
        setSavedLocationState(cloneDeep(location.state));
        setOpenParent('wireless');
        setNowAnchor('wireless-ssid');
      } else if (tab === "wireless-radio") {
        setOpenParent('wireless');
        setNowAnchor('wireless-radio');
      } else if (tab === "profiles-schedule") {
        setOpenParent('profiles');
        setNowAnchor('profiles-schedule');
      } else if (tab === "profiles-radius-server") {
        setOpenParent('profiles');
        setNowAnchor('profiles-radius-server');
      } else if (tab === "profiles-mac-address") {
        setOpenParent('profiles');
        setNowAnchor('profiles-mac-address');
      }
    }
  }, [location])

  useEffect(() => {
    if (selectCountry && selectCountry.value) {
      const timezoneList = getTimezoneList(selectCountry.value);
      setTimezoneList(timezoneList);

      if (selectCountry.value === originalCountryDomainData.value && originalTimezoneData) {
        setSelectTimezoneData(originalTimezoneData);
      } else {
        setSelectTimezoneData(timezoneList[0]);
      }
    }
  }, [selectCountry])

  const fetchGeneralData = () => {
    if (nowScope === 'Site') {
      const tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');

      const standalone = !tmpSelectedBread.orgs.id || Object.keys(tmpSelectedBread.orgs).length === 0;
      const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/general.read`;

      const restfulParamsConfig = {
        standalone: {
          orgId: tmpSelectedBread.realm?.id,
          siteId: tmpSelectedBread.site?.id,
        },
        nonStandalone: {
          entId: tmpSelectedBread.realm?.id,
          orgId: tmpSelectedBread.orgs?.id,
          siteId: tmpSelectedBread.site?.id,
        },
      };

      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = restfulParamsConfig[mode];

      sendRequest(apiUrl, readSiteGeneralUuid, restfulParams);
    }
  }

  function handleAddNtpServer() {
    if (ntpServers.length < 3) {
      setNtpServers([...ntpServers, ""]);
    }
  }

  function handleDeleteNtpServer(index) {
    const updated = ntpServers.filter((_, i) => i !== index);
    setNtpServers(updated);
  }

  function handleChangeNtpServer(value, index) {
    const updated = [...ntpServers];
    updated[index] = value;
    setNtpServers(updated);
  }

  const handleInput = (e, key) => {
    let tmpVal = e.target.value;
    switch (key) {
      case "scopeName":
        setScopeName(tmpVal);
        break;
      case "inventoryName":
        setInventoryName(tmpVal);
        const userErrorResult = validateDeviceAccount(tmpVal);
        setUserError(userErrorResult.valid ? "" : userErrorResult.msg);
        break;
      case "inventoryPW":
        setInventoryPW(tmpVal);
        const pwErrorResult = validateDeviceAccountPassword(tmpVal, inventoryName);
        setUserPWError(pwErrorResult.valid ? "" : pwErrorResult.msg);
        break;
      default:
        break;
    }
  };

  const readInventory = async () => {
    setNowUuid(readInventoryUuid);

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );
    let tmpResource = "";
    let tmpResourceAPIval = {};

    if (Object.keys(tmpSelectedBread.orgs).length === 0) {
      tmpResource = "/orgs/:orgId/sites/:siteId/settings/deviceAccount.read";
      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
        siteId: tmpSelectedBread.site.id,
      };
    } else {
      tmpResource =
        "/ents/:entId/orgs/:orgId/sites/:siteId/settings/deviceAccount.read";
      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id,
        siteId: tmpSelectedBread.site.id,
      };
    }

    sendRequest(tmpResource, readInventoryUuid, tmpResourceAPIval);
  };

  const updateInventory = async () => {
    setNowUuid(updateInventoryUuid);

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );
    let tmpResource = "";
    let tmpResourceAPIval = {};
    let tmpParam = { username: inventoryName, password: inventoryPW };

    if (Object.keys(tmpSelectedBread.orgs).length === 0) {
      tmpResource = "/orgs/:orgId/sites/:siteId/settings/deviceAccount.update";
      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
        siteId: tmpSelectedBread.site.id,
      };
    } else {
      tmpResource =
        "/ents/:entId/orgs/:orgId/sites/:siteId/settings/deviceAccount.update";
      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id,
        siteId: tmpSelectedBread.site.id,
      };
    }

    sendRequest(tmpResource, updateInventoryUuid, tmpResourceAPIval, {
      param: tmpParam,
    });
  };

  const handleCancelGeneral = () => {
    setScopeName(originalInfoData.siteName);
    setSelectCountry(originalCountryDomainData);
    setTimezoneList(originalTimezoneList)
    setSelectTimezoneData(originalTimezoneData);
    setNtpServers(originalNtpServers);
    setAddressData(originalInfoData.address);
    setInventoryName(originalDeviceAccountData.userName);
    setInventoryPW(originalDeviceAccountData.password);
    setUserError('')
    setUserPWError('')
  };

  const isGeneralSaveDisabled = () => {
    const hasChanges =
      scopeName !== originalInfoData.siteName ||
      addressData !== originalInfoData.address ||
      selectCountry?.value !== originalCountryDomainData?.value ||
      selectTimezoneData?.value !== originalTimezoneData?.value ||
      JSON.stringify(ntpServers) !== JSON.stringify(originalNtpServers) ||
      inventoryName !== originalDeviceAccountData.userName ||
      inventoryPW !== originalDeviceAccountData.password;

    return !scopeName.trim() || !inventoryName || Boolean(userError) ||
      !inventoryPW || Boolean(userPWError) || !hasChanges;
  };

  const handleSaveGeneral = () => {
    // if (!showEditGeneral || checkMockup === "true") {
    //   return;
    // }

    // if (isEmpty(scopeName)) {
    //   setCheckScopeName(false);
    //   return;
    // } else {
    //   setCheckScopeName(true);
    // }

    // let tmpSelectedBread = JSON.parse(
    //   localStorage.getItem("selectBreadcrumbs") || "{}"
    // );
    // let tmpItem = cloneDeep(tmpSelectedBread.site);
    // tmpItem.name = scopeName;
    // updateManageOrg(tmpItem);

    if (isGeneralSaveDisabled()) {
      return;
    }

    const tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');

    const standalone = !tmpSelectedBread.orgs.id || Object.keys(tmpSelectedBread.orgs).length === 0;
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/general.update`;

    const restfulParamsConfig = {
      standalone: {
        orgId: tmpSelectedBread.realm?.id,
        siteId: tmpSelectedBread.site?.id,
      },
      nonStandalone: {
        entId: tmpSelectedBread.realm?.id,
        orgId: tmpSelectedBread.orgs?.id,
        siteId: tmpSelectedBread.site?.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    const requestParams = {
      data: {
        info: {
          "siteId": originalInfoData.siteId,
          "siteName": scopeName,
          "address": addressData
        },
        countryDomain: {
          "countryIndex": selectCountry.value
        },
        timeConfig: {
          "timezone": selectTimezoneData.value,
          "ntpServer1": ntpServers[0] || "",
          "ntpServer2": ntpServers[1] || null,
          "ntpServer3": ntpServers[2] || null,
        },
        deviceAccount: {
          "userName": inventoryName,
          "password": inventoryPW
        }
      }
    }

    sendRequest(apiUrl, updateSiteGeneralUuid, restfulParams, requestParams);
  };

  // dataMap 先分開做 未來有機會一次整合全部的 dataMap 行為
  useEffect(() => {
    const response = getData(updateSiteGeneralUuid);
    if (response) {
      setOriginalInfoData((pre) => ({ ...pre, siteName: scopeName, address: addressData }))

      let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
      tmpSelectedBread.site.name = scopeName;
      localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));

      setOriginalCountryDomainData(selectCountry)
      setOriginalTimezoneList(timezoneList)
      setOriginalTimezoneData(selectTimezoneData)
      setOriginalNtpServers(ntpServers)
      setOriginalDeviceAccountData({ userName: inventoryName, password: inventoryPW })
    }
  }, [dataMap])

  const handleDeleteSetting = () => {
    setModalTitle("Delete " + scopeName);
    setModalContent("Enter your password to delete all settings.");
    setModalSaveBtn("Delete");
    setModalCancelBtn("Cancel");
    setOpenModalConfirmPw(true);
  };

  const updateManageOrg = async (item) => {
    setNowUuid(updateNameUuid);

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );
    let tmpResource = "";
    let tmpParam = "";
    let tmpResourceAPIval = {};

    if (Object.keys(tmpSelectedBread.orgs).length === 0) {
      // tmpResource = '/orgs/'+tmpSelectedBread.realm.id+'/sites/'+item.id
      tmpResource = "/orgs/:orgId/sites/:siteId.update";
      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
        siteId: item.id,
      };
    } else {
      // tmpResource = '/ents/'+tmpSelectedBread.realm.id+'/orgs/'+tmpSelectedBread.orgs.id+'/sites/'+item.id
      tmpResource = "/ents/:entId/orgs/:orgId/sites/:siteId.update";
      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id,
        siteId: item.id,
      };
    }

    tmpParam = { name: item.name };

    sendRequest(tmpResource, updateNameUuid, tmpResourceAPIval, {
      param: tmpParam,
    });
  };

  const delManageOrg = async (item) => {
    setNowUuid(deleteNameUuid);

    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );
    let tmpResource = "";
    let tmpResourceAPIval = {};

    if (Object.keys(tmpSelectedBread.orgs).length === 0) {
      // tmpResource = '/orgs/'+tmpSelectedBread.realm.id+'/sites/'+item.id
      tmpResource = "/orgs/:orgId/sites/:siteId.delete";
      tmpResourceAPIval = {
        orgId: tmpSelectedBread.realm.id,
        siteId: item.id,
      };
    } else {
      // tmpResource = '/ents/'+tmpSelectedBread.realm.id+'/orgs/'+tmpSelectedBread.orgs.id+'/sites/'+item.id
      tmpResource = "/ents/:entId/orgs/:orgId/sites/:siteId.delete";
      tmpResourceAPIval = {
        entId: tmpSelectedBread.realm.id,
        orgId: tmpSelectedBread.orgs.id,
        siteId: item.id,
      };
    }

    sendRequest(tmpResource, deleteNameUuid, tmpResourceAPIval);
  };

  // mockup 這段會有問題會進不來 初步判斷 nowUuid 會在某一個 side effect 變成空字串 導致進不來
  // 待重構
  useEffect(() => {
    if (nowUuid !== "") {
      switch (nowUuid) {
        case readInventoryUuid:
          if (dataMap[readInventoryUuid]?.code !== undefined) {
            setModalTitle("Action denied");
            setModalContent(dataMap[readInventoryUuid].message);
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("error");
            setOpenModalCheck(true);
          } else if (dataMap[readInventoryUuid]?.data !== undefined) {
            let tmpData = dataMap[readInventoryUuid].data;
            setInventoryName(tmpData.username);
            setInventoryPW(tmpData.password);
          }
          setNowUuid("");
          break;
        case updateInventoryUuid:
          if (dataMap[updateInventoryUuid]?.code !== undefined) {
            setModalTitle("Action denied");
            setModalContent(dataMap[updateInventoryUuid].message);
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("error");
            setOpenModalCheck(true);
          } else if (dataMap[updateInventoryUuid] !== undefined) {
            setModalTitle("Success");
            setModalContent("Your setting has been updated sucessfully.");
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("refresh");
            setOpenModalCheck(true);
          }
          setNowUuid("");
          break;
        case updateNameUuid:
          if (dataMap[updateNameUuid]?.code !== undefined) {
            setModalTitle("Action denied");
            setModalContent(dataMap[updateNameUuid].message);
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("error");
            setOpenModalCheck(true);
          } else if (dataMap[updateNameUuid]?.data !== undefined) {
            let tmpSelectedBread = JSON.parse(
              localStorage.getItem("selectBreadcrumbs") || "{}"
            );
            tmpSelectedBread.site.name = scopeName;
            localStorage.setItem(
              "selectBreadcrumbs",
              JSON.stringify(tmpSelectedBread)
            );

            const targetScope = cloneDeep(scopeData.site);
            const updateScope = targetScope.map((item) =>
              item.id === tmpSelectedBread.site.id
                ? { ...item, name: scopeName }
                : item
            );
            setScopeData((prev) => ({
              ...prev,
              site: updateScope,
            }));
          }
          setNowUuid("");
          updateInventory();
          break;
        case deleteNameUuid:
          if (dataMap[deleteNameUuid]?.code !== undefined) {
            setModalTitle("Action denied");
            setModalContent(dataMap[deleteNameUuid].message);
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("error");
            setOpenModalCheck(true);
          } else if (dataMap[deleteNameUuid] !== undefined) {
            setModalTitle("Success");
            setModalContent("It will redirect to the realm home page later.");
            setModalCancelBtn("");
            setModalSaveBtn("OK");
            setModalFrom("navigate");
            setOpenModalCheck(true);
          }
          setNowUuid("");
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  const handleShowDevicePw = (e) => {
    let tmpVal = showDevicePw;
    setShowDevicePw(!tmpVal);
  };

  const handleCheckModalAction = (value) => {
    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );

    if (value === "ok") {
      if (modalFrom === "navigate") {
        let tmpScope = cloneDeep(scopeData);
        tmpScope.site = [];
        setScopeData(tmpScope);
        let tmpSelectedBread = JSON.parse(
          localStorage.getItem("selectBreadcrumbs") || "{}"
        );
        tmpSelectedBread.site = {};
        localStorage.setItem(
          "selectBreadcrumbs",
          JSON.stringify(tmpSelectedBread)
        );
        setBread(tmpSelectedBread);

        navigate("/org/sites");
      } else if (modalFrom === "refresh") {
        // window.location.reload();
      }
    }
  };

  const handlePasswordModalAction = (value) => {
    let tmpSelectedBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}"
    );

    if (value === "ok") {
      let tmpItem = cloneDeep(tmpSelectedBread.site);
      delManageOrg(tmpItem);
    }
  };

  return (
    <>
      <div className={mainStyle["anchorArea"]}>
        <div className={mainStyle["anchorBody"]}>
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor:
                  openParent === "wireless" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleParentClick("wireless")}
            >
              <img
                src={
                  openParent === "wireless" ? btn_wireless_light : btn_wireless
                }
                alt="wireless"
                style={{ width: "40px" }}
              />
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    openParent === "wireless"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Wireless
              </span>
            </div>

            {openParent === "wireless" &&
              [
                { key: "wireless-ssid", label: "SSID", showArrow: true },
                { key: "wireless-radio", label: "Radio", showArrow: true },
              ].map(
                (item) => (
                  <div key={item.key}>
                    <div
                      className={mainStyle["subAnchorItem"]}
                      style={{
                        backgroundColor:
                          nowAnchor === item.key
                            ? theme.palette.primary.subBlue
                            : "",
                      }}
                      onClick={() => handleSetNowAnchor(item.key)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: theme.palette.primary.main,
                              margin: "2.5px 10px 2.5px 0",
                              borderRadius: "2px",
                              display: "inline-block",
                            }}
                          />
                          <span
                            className={mainStyle["subAnchorItem_text"]}
                            style={{ color: theme.palette.primary.main }}
                          >
                            {item.label}
                          </span>
                        </div>
                        {item.showArrow && <img src={icn_expandright} />}
                      </div>
                    </div>
                  </div>
                )
              )}
          </Box>
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor:
                  openParent === "wired" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleParentClick("wired")}
            >
              <img
                src={openParent === "wired" ? btn_wired_light : btn_wired}
                alt="wired"
                style={{ width: "40px" }}
              />
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    openParent === "wired"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Wired
              </span>
            </div>

            {openParent === "wired" &&
              [
                { key: "wired-basic", label: "Basic", showArrow: true },
                { key: "wired-voiceVlan", label: "Voice VLAN", showArrow: true },
                {
                  key: "wired-qualityOfService",
                  label: "Quality of Service",
                  showArrow: true,
                  showProIcon: true,
                },
                {
                  key: "wired-ipv4Acl",
                  label: "IPv4 ACL",
                  showArrow: true,
                  showProIcon: true,
                },
                {
                  key: "wired-portManagement",
                  label: "Port Management",
                  showArrow: true,
                },
                {
                  key: "wired-configProvisioning",
                  label: "Config Provisioning",
                  showArrow: true,
                },
              ].map((item) => (
                <div key={item.key}>
                  <div
                    className={mainStyle["subAnchorItem"]}
                    style={{
                      backgroundColor:
                        nowAnchor === item.key
                          ? theme.palette.primary.subBlue
                          : "",
                    }}
                    onClick={() => handleSetNowAnchor(item.key)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span
                          style={{
                            width: "6px",
                            height: "6px",
                            backgroundColor: theme.palette.primary.main,
                            margin: "2.5px 10px 2.5px 0",
                            borderRadius: "2px",
                            display: "inline-block",
                          }}
                        />
                        <span
                          className={mainStyle["subAnchorItem_text"]}
                          style={{ color: theme.palette.primary.main }}
                        >
                          {item.label}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {item.showProIcon && (
                          isUnityProSubscribed
                            ? <ProIcon />
                            : item.key === "wired-qualityOfService"
                              ? qosHasNonDefaultValue !== null && (
                                qosHasNonDefaultValue ? (
                                  <Tooltip title="Pro feature disabled" arrow>
                                    <ProOrangeOutlineIcon />
                                  </Tooltip>
                                ) : <ProIcon />
                              )
                              : ipv4AclRuleCount !== null && (
                                ipv4AclRuleCount === 0 ? <ProIcon /> : (
                                  <Tooltip title="Pro feature disabled" arrow>
                                    <ProOrangeOutlineIcon />
                                  </Tooltip>
                                )
                              )
                        )}
                        {item.showArrow && <img src={icn_expandright} />}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </Box>

          {/* Captive portal Start */}
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor: nowAnchor === "captive-portal" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleAnchor("captive-portal")}
            >
              <img
                src={nowAnchor === "captive-portal" ? btn_captive_portal_light : btn_captive_portal}
                alt="captive portal"
                style={{ width: "40px" }}
              />
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    nowAnchor === "captive-portal"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Captive Portal
              </span>
            </div>
          </Box>
          {/* Captive portal End */}

          {/* Notifications */}
          {showEditGeneral && <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor: nowAnchor === "notifications" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleAnchor("notifications")}
            >
              <img
                src={nowAnchor === "notifications" ? btn_alert_light : btn_alert}
                alt="notifications"
                style={{ width: "40px" }}
              />
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    nowAnchor === "notifications"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Notifications
              </span>
            </div>
          </Box>}

          {/* Profiles */}
          <Box>
            <ParentMenu
              target={leftMenu.profiles.key}
              label={leftMenu.profiles.label}
              openParent={openParent}
              handleParentClick={handleParentClick}
              lightIcon={btn_profiles_light}
              icon={btn_profiles}
            />
            {
              openParent === "profiles" && leftMenu.profiles.subs.map((item) => (
                <SubMenu
                  key={item.key}
                  nowAnchor={nowAnchor}
                  item={item}
                  setNowAnchor={setNowAnchor}
                  ipAddressProfileCount={ipAddressProfileCount}
                  ldapProfileCount={ldapProfileCount}
                />
              ))
            }
          </Box>

          {/* Scheduled Reboot */}
          <Box>
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor: nowAnchor === "scheduled-reboot" ? theme.palette.primary.main : "",
                cursor: "pointer",
              }}
              onClick={() => handleAnchor("scheduled-reboot")}
            >
              {nowAnchor === "scheduled-reboot" ? (
                <ScheduleLightIcon
                  style={{
                    width: "40px",
                    height: "40px",
                    boxSizing: "border-box",
                  }}
                />
              ) : (
                <ScheduleIcon
                  style={{
                    width: "40px",
                    height: "40px",
                    boxSizing: "border-box",
                  }}
                />
              )}
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    nowAnchor === "scheduled-reboot"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                Scheduled Reboot
              </span>
            </div>
          </Box>

          <Divider
            orientation="horizontal"
            sx={{
              borderColor: theme.palette.primary.greyThread,
              height: "2px",
            }}
          />
          <Box
            onClick={() => {
              handleAnchor("account");
            }}
          >
            <div
              className={mainStyle["anchorItem"]}
              style={{
                backgroundColor:
                  nowAnchor === "account" ? theme.palette.primary.main : "",
              }}
            >
              <img
                src={nowAnchor === "account" ? btn_general_light : btn_general}
                style={{ width: "40px" }}
                alt=""
              />
              <span
                className={mainStyle["anchorItem_text"]}
                style={{
                  color:
                    nowAnchor === "account"
                      ? theme.palette.primary.white
                      : theme.palette.primary.main,
                }}
              >
                General
              </span>
            </div>
          </Box>
        </div>
      </div >
      <div className={mainStyle["userArea"]}>
        <Grid style={{ marginBottom: "20px" }}>
          <Box id="section1">
            {/* style={{height:'1200px'}}  */}
            {nowAnchor === "account" ? (
              <>
                <div className={mainStyle["titleArea"]}>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      ml: 1,
                      mt: 2,
                      color: theme.palette.primary.main,
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.customFontSize[5],
                      fontWeight: theme.typography.customFontWeight[3],
                    }}
                  >
                    General Settings
                  </Typography>
                </div>
                <div className={mainStyle["contentArea"]}>
                  <Grid container className={`${mainStyle["optionArea"]}`}>
                    <Typography
                      sx={{
                        width: "190px",
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      {nowScope} Name
                    </Typography>
                    {!showEditGeneral && (
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: "10px" }}
                      >
                        {scopeName}
                      </Typography>
                    )}
                    {showEditGeneral && (
                      <TextField
                        value={scopeName}
                        size="small"
                        onChange={(e) => handleInput(e, "scopeName")}
                        variant="outlined"
                        error={!checkScopeName}
                        inputProps={{
                          style: {
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                            fontWeight: theme.typography.customFontWeight[1],
                            fontFamily: theme.typography.fontFamily,
                          }
                        }}
                        sx={{
                          mr: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px", // 改變圓角
                            height: "29px",
                            "& fieldset": {
                              borderColor: theme.palette.primary.greyFifth, // 預設狀態的框線顏色
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.mainHover,
                            },
                            "&.Mui-focused fieldset": {
                              border: "1px solid",
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                        style={{ width: "325px", paddingLeft: "10px" }}
                      />
                    )}
                  </Grid>
                  <Grid container className={`${mainStyle['optionArea']}`}>
                    <Typography
                      sx={{
                        width: '190px',
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Select Country
                    </Typography>
                    {!showEditGeneral &&
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        {(selectCountry && selectCountry.label) || '-'}
                      </Typography>
                    }
                    {showEditGeneral &&
                      <Box sx={{ marginLeft: '10px' }}>
                        <AutocompleteSelect
                          width="318px"
                          placeholder="Select a Country"
                          options={selectCountryList}
                          value={selectCountry}
                          setValue={setSelectCountry}
                        />
                      </Box>
                    }
                  </Grid>
                  <Grid container className={`${mainStyle['optionArea']}`}>
                    <Typography
                      sx={{
                        width: '190px',
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Time Zone
                    </Typography>
                    {!showEditGeneral &&
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        {(selectTimezoneData && selectTimezoneData.value) || '-'}
                      </Typography>
                    }
                    {showEditGeneral &&
                      <Box sx={{ marginLeft: '10px' }}>
                        <MenuSelect
                          width="318px"
                          placeholder="Select a Time Zone"
                          value={selectTimezoneData && selectTimezoneData.value}
                          onChange={(opt) => setSelectTimezoneData(opt)}
                          options={timezoneList}
                        />
                      </Box>
                    }
                  </Grid>
                  <Grid container className={`${mainStyle['optionArea']}`}>
                    <Typography
                      sx={{
                        width: '190px',
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      NTP Server
                    </Typography>
                    {!showEditGeneral &&
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        {ntpServers[0] || "-"}
                      </Typography>
                    }
                    {showEditGeneral &&
                      <TextField
                        value={ntpServers[0] || ""}
                        size="small"
                        onChange={(e) => handleChangeNtpServer(e.target.value, 0)}
                        variant="outlined"
                        inputProps={{
                          style: {
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                            fontWeight: theme.typography.customFontWeight[1],
                            fontFamily: theme.typography.fontFamily,
                          }
                        }}
                        sx={{
                          mr: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px', // 改變圓角
                            height: "29px",
                            '& fieldset': {
                              borderColor: theme.palette.primary.greyFifth,
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.mainHover,
                            },
                            "&.Mui-focused fieldset": {
                              border: '1px solid',
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                        style={{ width: '325px', paddingLeft: '10px', }}
                        autoComplete="off"
                      />
                    }
                  </Grid>
                  {ntpServers.map((val, index) => {
                    if (index === 0) return null;

                    return (
                      <Grid container className={mainStyle['optionArea']} key={index}>
                        <Typography sx={{ width: "190px" }} />

                        {!showEditGeneral &&
                          <Typography
                            sx={{
                              color: theme.palette.primary.greyPrimary,
                              fontSize: theme.typography.customFontSize[2],
                              fontWeight: theme.typography.customFontWeight[1],
                              fontFamily: theme.typography.fontFamily,
                            }}
                            style={{ marginLeft: '10px' }}
                          >
                            {val}
                          </Typography>
                        }

                        {showEditGeneral && <>
                          <TextField
                            size="small"
                            value={val}
                            onChange={(e) => handleChangeNtpServer(e.target.value, index)}
                            variant="outlined"
                            inputProps={{
                              style: {
                                color: theme.palette.primary.greyPrimary,
                                fontSize: theme.typography.customFontSize[2],
                                fontWeight: theme.typography.customFontWeight[1],
                                fontFamily: theme.typography.fontFamily,
                              }
                            }}
                            sx={{
                              mr: 1,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                height: "29px",
                                "& fieldset": {
                                  borderColor: theme.palette.primary.greyFifth,
                                },
                                "&:hover fieldset": {
                                  borderColor: theme.palette.primary.mainHover,
                                },
                                "&.Mui-focused fieldset": {
                                  border: "1px solid",
                                  borderColor: theme.palette.primary.main,
                                },
                              },
                            }}
                            style={{ width: "295px", paddingLeft: "10px" }}
                            autoComplete="off"
                          />

                          <StyledIconButton onClick={() => handleDeleteNtpServer(index)}>
                            <UnverifiedIcon />
                          </StyledIconButton>
                        </>
                        }
                      </Grid>
                    );
                  })}
                  {showEditGeneral &&
                    <Grid container className={`${mainStyle['optionArea']}`}>
                      <Typography sx={{ width: '450px' }} />
                      <OutlineRectangleButton
                        onMouseEnter={() => setIsAddHovered(true)}
                        onMouseLeave={() => setIsAddHovered(false)}
                        startIcon={
                          isAddHovered ? <AddHoverIcon /> : <AddIcon />
                        }
                        width="54px"
                        height="28px"
                        onClick={handleAddNtpServer}
                        disabled={ntpServers.length >= 3}
                      >
                        Add
                      </OutlineRectangleButton>
                    </Grid>
                  }
                  <Grid
                    container
                    className={mainStyle['optionArea']}
                    sx={{ height: 'auto', alignItems: 'flex-start' }}
                  >
                    <Typography
                      sx={{
                        width: '190px',
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Address
                    </Typography>
                    {!showEditGeneral &&
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: '10px' }}
                      >
                        {addressData || '-'}
                      </Typography>
                    }
                    {showEditGeneral && <Box sx={{ width: '500px', marginLeft: '10px' }}>
                      <TextAreaField
                        value={addressData}
                        onChange={setAddressData}
                        placeholder="Apply to all managed device"
                        rows={10}
                      />
                    </Box>
                    }
                  </Grid>
                </div>
                <div className={mainStyle["titleArea"]}>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      ml: 1,
                      mt: 2,
                      color: theme.palette.primary.main,
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.customFontSize[5],
                      fontWeight: theme.typography.customFontWeight[3],
                    }}
                  >
                    Device Account
                  </Typography>
                </div>
                <div className={mainStyle["contentArea"]}>
                  <Grid container className={`${mainStyle["optionArea"]}`}>
                    <Typography
                      sx={{
                        width: "190px",
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Username
                      <Typography component="span" sx={{ color: theme.palette.primary.redPrimary }}>
                        *
                      </Typography>
                    </Typography>
                    {!showEditGeneral && (
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                        style={{ marginLeft: "10px" }}
                      >
                        {inventoryName || '-'}
                      </Typography>
                    )}
                    {showEditGeneral && (
                      <TextField
                        value={inventoryName}
                        size="small"
                        onChange={(e) => handleInput(e, "inventoryName")}
                        variant="outlined"
                        error={userError}
                        inputProps={{
                          style: {
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                            fontWeight: theme.typography.customFontWeight[1],
                            fontFamily: theme.typography.fontFamily,
                          }
                        }}
                        sx={{
                          mr: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px", // 改變圓角
                            height: "29px",
                            "& fieldset": {
                              borderColor: theme.palette.primary.greyFifth, // 預設狀態的框線顏色
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.mainHover,
                            },
                            "&.Mui-focused fieldset": {
                              border: "1px solid",
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                        style={{ width: "325px", paddingLeft: "10px" }}
                      />
                    )}
                  </Grid>
                  <Grid container className={`${mainStyle["optionArea"]}`}>
                    <Typography
                      sx={{
                        width: "190px",
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[0],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Password
                      <Typography component="span" sx={{ color: theme.palette.primary.redPrimary }}>
                        *
                      </Typography>
                    </Typography>
                    {!showEditGeneral && (
                      <Box sx={{ display: "flex" }}>
                        <Typography
                          sx={{
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                            fontWeight: theme.typography.customFontWeight[1],
                            fontFamily: theme.typography.fontFamily,
                          }}
                          style={{ marginLeft: "10px" }}
                        >
                          {showDevicePw ? inventoryPW : '•'.repeat(inventoryPW?.length || 8)}
                        </Typography>
                        <Box sx={{ marginLeft: "8px" }}>
                          <StyledIconButton onClick={handleShowDevicePw}>
                            {!showDevicePw ? <HideIcon /> : <ShowIcon />}
                          </StyledIconButton>
                        </Box>
                      </Box>
                    )}
                    {showEditGeneral && (
                      <TextField
                        value={inventoryPW}
                        size="small"
                        onChange={(e) => handleInput(e, "inventoryPW")}
                        variant="outlined"
                        error={userPWError}
                        inputProps={{
                          style: {
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                            fontWeight: theme.typography.customFontWeight[1],
                            fontFamily: theme.typography.fontFamily,
                          }
                        }}
                        type={showDevicePw ? "text" : "password"}
                        sx={{
                          mr: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px", // 改變圓角
                            height: "29px",
                            "& fieldset": {
                              borderColor: theme.palette.primary.greyFifth, // 預設狀態的框線顏色
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.mainHover,
                            },
                            "&.Mui-focused fieldset": {
                              border: "1px solid",
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                        style={{ width: "325px", paddingLeft: "10px" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {!showDevicePw && (
                                <HideIcon
                                  onClick={handleShowDevicePw}
                                  style={{ cursor: "pointer" }}
                                />
                              )}
                              {showDevicePw && (
                                <ShowIcon
                                  onClick={handleShowDevicePw}
                                  style={{ cursor: "pointer" }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                  <Grid container className={`${mainStyle['optionArea']}`}>
                    <Typography
                      sx={{
                        width: '190px',
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[1],
                        fontWeight: theme.typography.customFontWeight[1],
                        fontFamily: theme.typography.fontFamily,
                      }}
                    />
                    {userPWError &&
                      <Typography
                        sx={{
                          fontSize: theme.typography.customFontSize[3],
                          color: theme.palette.primary.redPrimary,
                        }}
                        style={{ marginLeft: '10px' }}
                      >{userPWError}
                      </Typography>
                    }
                  </Grid>
                  {showDeleteGeneral && (
                    <Grid container className={`${mainStyle["optionArea"]}`}>
                      <RemoveIcon
                        onClick={() => handleDeleteSetting()}
                        style={{ cursor: "pointer", margin: "0 6px 0 2px" }}
                      />
                      <Typography
                        onClick={() => handleDeleteSetting()}
                        style={{ cursor: "pointer" }}
                        sx={{
                          width: "190px",
                          color: theme.palette.primary.redPrimary,
                          fontSize: theme.typography.customFontSize[1],
                          fontWeight: theme.typography.customFontWeight[1],
                          fontFamily: theme.typography.fontFamily,
                        }}
                      >
                        Delete {nowScope}
                      </Typography>
                    </Grid>
                  )}
                </div>
                {showEditGeneral && (
                  <Box>
                    <Grid
                      container
                      style={{ justifyContent: "center", width: "100%" }}
                    >
                      <Box style={{ margin: "10px" }}>
                        <OutlineButton
                          width="135px"
                          height="42px"
                          onClick={() => handleCancelGeneral()}
                          disabled={showEditGeneral ? false : true}
                        >
                          Cancel
                        </OutlineButton>
                      </Box>
                      <Box style={{ margin: "10px" }}>
                        <SolidButton
                          width="135px"
                          height="42px"
                          onClick={() => handleSaveGeneral()}
                          disabled={isGeneralSaveDisabled()}
                        >
                          Save
                        </SolidButton>
                      </Box>
                    </Grid>
                  </Box>
                )}
              </>
            ) : (
              <></>
            )}
            {nowAnchor === "wireless-ssid" && <WirelessSSID {...{ showEditGeneral, unSavedBlocker, onGoToPage: handleGoToPage }} />}
            {nowAnchor === "wireless-radio" && <WirelessRadio {...{ showEditGeneral, selectCountry }} />}
            {nowAnchor === "wired-basic" && <WiredBasic {...{ showEditGeneral }} />}
            {nowAnchor === "wired-qualityOfService" && (
              <WiredQualityOfService
                showEditGeneral={showEditGeneral}
                onQosDefaultStateChange={setQosHasNonDefaultValue}
              />
            )}
            {nowAnchor === "wired-ipv4Acl" && (
              <WiredIPv4ACL
                showEditGeneral={showEditGeneral}
                onIpv4AclRuleCountChange={setIpv4AclRuleCount}
              />
            )}
            {nowAnchor === "wired-voiceVlan" && <VoiceVlan {...{ showEditGeneral }} />}
            {nowAnchor === "captive-portal" && <CaptivePortal {...{ showEditGeneral }} />}
            {nowAnchor === "notifications" && <Notifications {...{ showEditGeneral }} />}
            {nowAnchor === "wired-portManagement" && <WiredPortManagement {
              ...{
                savedLocationState,
                clearSavedLocationState: () => setSavedLocationState(null),
                showEditGeneral,
                unSavedBlocker,
                onGoToPage: handleGoToPage
              }
            }
            />
            }
            {nowAnchor === "wired-configProvisioning" && <WiredConfigProvisioning {...{ showEditGeneral, onGoToPage: handleGoToPage }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey.port) && <ProfilesPort {...{ showEditGeneral }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey.schedule) && <ProfileSchedule {...{ showEditGeneral }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey["mac-address"]) && <ProfileMacAddress {...{ showEditGeneral }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey["ip-address"]) && (
              <ProfileIpAddress
                showEditGeneral={showEditGeneral}
                onIpAddressProfileCountChange={setIpAddressProfileCount}
              />
            )}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey["user-account"]) && <ProfileUserAccount {...{ showEditGeneral }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey["radius-server"]) && <ProfileRadiusServer {...{ showEditGeneral }} />}
            {nowAnchor === leftMenu.profiles.getSubKey(leftMenu.profiles.subKey["ldap-server"]) && (
              <ProfileLdapServer
                showEditGeneral={showEditGeneral}
                onLdapProfileCountChange={setLdapProfileCount}
              />
            )}
            {nowAnchor === "scheduled-reboot" && <ScheduledReboot {...{ showEditGeneral }} />}
          </Box>
        </Grid>
      </div>

      <ModalContainer
        display={openModalCheck}
        modalWidth={"550px"} //550px, 750px, empty=1000px,
        children={
          <ModalforCheck
            title={modalTitle}
            content={modalContent}
            cancelBtn={modalCancelBtn}
            saveBtn={modalSaveBtn}
            setOpenModal={setOpenModalCheck}
            onBottonClick={handleCheckModalAction}
          />
        }
      />
      <ModalContainer
        display={openModalConfirmPw}
        modalWidth={"500px"} //550px, 750px, empty=1000px,
        children={
          <ModalConfirmPw
            title={modalTitle}
            content={modalContent}
            cancelBtn={modalCancelBtn}
            saveBtn={modalSaveBtn}
            setOpenModal={setOpenModalConfirmPw}
            originUser={originUser}
            onBottonClick={handlePasswordModalAction}
          />
        }
      />
    </>
  );
};

export default SiteLevel;
